import React, { useState } from 'react';
import styled from 'styled-components';
import { 
  Play, 
  Mail, 
  Clock, 
  GitBranch, 
  Filter, 
  Search,
  Repeat,
  Zap,
  Database,
  FileText
} from 'lucide-react';
import { motion } from 'framer-motion';

interface NodePaletteProps {
  onNodeDragStart?: (nodeType: string, nodeData: NodeData) => void;
}

interface NodeData {
  type: string;
  label: string;
  config: Record<string, unknown>;
  color: string;
}

interface NodeTemplate {
  type: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  category: 'trigger' | 'action' | 'control' | 'filter';
  defaultConfig: Record<string, unknown>;
}

const NODE_TEMPLATES: NodeTemplate[] = [
  // TRIGGERS
  {
    type: 'trigger',
    label: 'Trigger Manual',
    description: 'Inicia o fluxo manualmente',
    icon: <Play size={20} />,
    color: '#00ff88',
    category: 'trigger',
    defaultConfig: { triggerType: 'manual' }
  },
  {
    type: 'emailReceived',
    label: 'Email Recebido',
    description: 'Acionado quando recebe email',
    icon: <Mail size={20} />,
    color: '#00ff88',
    category: 'trigger',
    defaultConfig: { triggerType: 'emailReceived', labelFilter: 'INBOX' }
  },
  {
    type: 'scheduledTrigger',
    label: 'Agendamento',
    description: 'Executa em horários específicos',
    icon: <Zap size={20} />,
    color: '#00ff88',
    category: 'trigger',
    defaultConfig: { triggerType: 'scheduled', cronExpression: '0 9 * * *' }
  },

  // ACTIONS
  {
    type: 'sendEmail',
    label: 'Enviar Email',
    description: 'Envia email via Gmail API',
    icon: <Mail size={20} />,
    color: '#ff3b30',
    category: 'action',
    defaultConfig: { to: '', subject: '', body: '' }
  },
  {
    type: 'saveToDatabase',
    label: 'Salvar em DB',
    description: 'Armazena dados no banco',
    icon: <Database size={20} />,
    color: '#ff3b30',
    category: 'action',
    defaultConfig: { table: 'leads', fields: {} }
  },
  {
    type: 'createLead',
    label: 'Criar Lead',
    description: 'Registra novo lead no CRM',
    icon: <FileText size={20} />,
    color: '#ff3b30',
    category: 'action',
    defaultConfig: { nome: '', telefone: '', email: '', status: 'novo' }
  },

  // CONTROL FLOW
  {
    type: 'delay',
    label: 'Delay',
    description: 'Pausa a execução',
    icon: <Clock size={20} />,
    color: '#ffaa00',
    category: 'control',
    defaultConfig: { duration: 5, unit: 'seconds' }
  },
  {
    type: 'condition',
    label: 'Condição',
    description: 'Bifurca baseado em condição',
    icon: <GitBranch size={20} />,
    color: '#00aaff',
    category: 'control',
    defaultConfig: { variable: '', operator: '==', value: '' }
  },
  {
    type: 'loop',
    label: 'Loop',
    description: 'Repete ações para cada item',
    icon: <Repeat size={20} />,
    color: '#ff6259',
    category: 'control',
    defaultConfig: { items: [], maxIterations: 10 }
  },

  // FILTERS
  {
    type: 'filter',
    label: 'Filtro',
    description: 'Filtra itens por critério',
    icon: <Filter size={20} />,
    color: '#9b59b6',
    category: 'filter',
    defaultConfig: { field: '', operator: 'contains', value: '' }
  },
];

const CATEGORIES = [
  { id: 'trigger' as const, label: 'Gatilhos', emoji: '⚡' },
  { id: 'action' as const, label: 'Ações', emoji: '🚀' },
  { id: 'control' as const, label: 'Controle', emoji: '🔀' },
  { id: 'filter' as const, label: 'Filtros', emoji: '🔍' },
];

const NodePalette: React.FC<NodePaletteProps> = ({ onNodeDragStart }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const filteredNodes = NODE_TEMPLATES.filter(node => {
    const matchesSearch = node.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          node.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !activeCategory || node.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const handleDragStart = (event: React.DragEvent, node: NodeTemplate) => {
    const nodeData = {
      type: node.type,
      label: node.label,
      config: node.defaultConfig,
      color: node.color,
    };

    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('application/reactflow', JSON.stringify(nodeData));
    
    if (onNodeDragStart) {
      onNodeDragStart(node.type, nodeData);
    }
  };

  return (
    <Container>
      <Header>
        <Title>Componentes</Title>
        <Subtitle>Arraste para o canvas</Subtitle>
      </Header>

      <SearchContainer>
        <SearchIcon>
          <Search size={16} />
        </SearchIcon>
        <SearchInput
          type="text"
          placeholder="Buscar componentes..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </SearchContainer>

      <CategoriesContainer>
        <CategoryChip
          $active={!activeCategory}
          onClick={() => setActiveCategory(null)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Todos
        </CategoryChip>
        {CATEGORIES.map(category => (
          <CategoryChip
            key={category.id}
            $active={activeCategory === category.id}
            onClick={() => setActiveCategory(activeCategory === category.id ? null : category.id)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <span>{category.emoji}</span>
            {category.label}
          </CategoryChip>
        ))}
      </CategoriesContainer>

      <NodesContainer>
        {filteredNodes.length === 0 ? (
          <EmptyState>
            <EmptyIcon>🔍</EmptyIcon>
            <EmptyText>Nenhum componente encontrado</EmptyText>
            <EmptyHint>Tente buscar por outro termo</EmptyHint>
          </EmptyState>
        ) : (
          filteredNodes.map((node, index) => (
            <NodeCard
              key={`${node.type}-${index}`}
              draggable
              onDragStart={(e: React.DragEvent) => handleDragStart(e, node)}
              $color={node.color}
              style={{ 
                animationDelay: `${index * 0.05}s`,
                animation: 'fadeInUp 0.3s ease-out forwards',
                opacity: 0
              }}
            >
              <NodeIcon $color={node.color}>
                {node.icon}
              </NodeIcon>
              <NodeContent>
                <NodeLabel>{node.label}</NodeLabel>
                <NodeDescription>{node.description}</NodeDescription>
              </NodeContent>
              <DragHandle>⋮⋮</DragHandle>
            </NodeCard>
          ))
        )}
      </NodesContainer>

      <Footer>
        <FooterText>
          💡 Dica: Arraste e solte no canvas
        </FooterText>
      </Footer>
    </Container>
  );
};

const Container = styled.div`
  width: 280px;
  height: 100%;
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
    height: 300px;
    border-right: none;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }
`;

const Header = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const Title = styled.h3`
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 ${({ theme }) => theme.spacing[1]} 0;
`;

const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

const SearchContainer = styled.div`
  position: relative;
  padding: ${({ theme }) => theme.spacing[3]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const SearchIcon = styled.div`
  position: absolute;
  left: ${({ theme }) => theme.spacing[5]};
  top: 50%;
  transform: translateY(-50%);
  color: ${({ theme }) => theme.colors.textSecondary};
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[8]};
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}20;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const CategoriesContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  overflow-x: auto;
  flex-wrap: nowrap;

  &::-webkit-scrollbar {
    height: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border};
    border-radius: 2px;
  }
`;

const CategoryChip = styled(motion.button)<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[3]};
  background: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.background};
  color: ${({ $active, theme }) => $active ? theme.colors.textPrimary : theme.colors.textSecondary};
  border: 1px solid ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.border};
  border-radius: 9999px;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  cursor: pointer;
  white-space: nowrap;
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ $active, theme }) => $active ? theme.colors.primaryHover : theme.colors.primary}20;
  }
`;

const NodesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing[3]};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.background};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border};
    border-radius: 3px;

    &:hover {
      background: ${({ theme }) => theme.colors.primary};
    }
  }
`;

const NodeCard = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  cursor: grab;
  transition: all ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};

  &:active {
    cursor: grabbing;
  }

  &:hover {
    border-color: ${({ $color }) => $color};
    box-shadow: 0 4px 12px ${({ $color }) => $color}30;
    transform: scale(1.02) translateY(-2px);
  }
`;

const NodeIcon = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: ${({ $color }) => $color}20;
  color: ${({ $color }) => $color};
  border-radius: 8px;
  flex-shrink: 0;
`;

const NodeContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const NodeLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: ${({ theme }) => theme.spacing[1]};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const NodeDescription = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DragHandle = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 16px;
  line-height: 1;
  cursor: grab;
  user-select: none;
  opacity: 0.5;
  transition: opacity ${({ theme }) => theme.animations.duration.fast} ${({ theme }) => theme.animations.easing.easeInOut};

  ${NodeCard}:hover & {
    opacity: 1;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing[8]} ${({ theme }) => theme.spacing[4]};
  text-align: center;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  opacity: 0.5;
`;

const EmptyText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  margin: 0 0 ${({ theme }) => theme.spacing[1]} 0;
`;

const EmptyHint = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

const Footer = styled.div`
  padding: ${({ theme }) => theme.spacing[3]};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
`;

const FooterText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
  margin: 0;
`;

export default NodePalette;
